/**
 * FrozenScreen — professional restriction overlay shown to students
 * whose accounts have been temporarily frozen by an admin.
 *
 * Design intent (iter 159):
 *  • Native to the LW Student Portal — same light paper background,
 *    same brand type + colours, same shadow language. Not a bespoke
 *    dark-mode surface.
 *  • Behind the modal we render a static, blurred + dimmed mockup of
 *    the actual portal home so the student recognises where they are.
 *    Because the backend rejects every real portal endpoint with 423,
 *    a mockup is the only faithful "vibe" we can safely show.
 *  • The modal is undismissable: no X, no click-outside close, no ESC.
 *    Only "Sign out" ends the session — everything else in the app is
 *    unreachable until an admin unfreezes.
 *  • Every text container clamps / wraps so long batch names or emails
 *    can never overflow the viewport on mobile / tablet / desktop.
 */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Lock, Phone, MessageCircle, Mail, Clock, LogOut, Loader2,
  ShieldAlert, Copy, Check, Sparkles, BookOpen,
} from "lucide-react";
import { API, useAuth } from "../contexts/AuthContext";

export default function FrozenScreen() {
  const { logout, user } = useAuth();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/me/frozen-info`)
      .then((r) => { if (!cancelled) setInfo(r.data); })
      .catch(() => { if (!cancelled) setErr("Could not load account status. Please refresh."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // Undismissable: swallow ESC key so screen readers / keyboard users
  // can't reach the underlying (blurred) portal.
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); } };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, []);

  return (
    <div className="fx-shell" data-testid="frozen-screen">
      <PortalMockup studentName={info?.student_name || user?.name || "Student"} />
      <div className="fx-dim" aria-hidden />
      <div className="fx-modal-wrap">
        {loading ? (
          <div className="fx-modal fx-modal--loading" data-testid="frozen-loading">
            <Loader2 size={26} style={{ color: "#3b82f6" }} className="animate-spin" />
          </div>
        ) : (
          <FrozenModal info={info} err={err} user={user} logout={logout} />
        )}
      </div>
      <FrozenScreenStyles />
    </div>
  );
}

// ── Blurred/dimmed mockup of the actual portal home ────────────────
function PortalMockup({ studentName }) {
  return (
    <div className="fx-portal" aria-hidden data-testid="frozen-backdrop">
      {/* Left dock */}
      <aside className="fx-dock">
        <div className="fx-dock-brand" />
        {[BookOpen, Sparkles, Sparkles, BookOpen, Sparkles, BookOpen].map((_, i) => (
          <div key={i} className="fx-dock-item" />
        ))}
      </aside>

      {/* Main */}
      <main className="fx-main">
        <div className="fx-top">
          <div className="fx-crumb">TUESDAY · AUG 18</div>
          <div className="fx-title">Good afternoon,<br /><span className="fx-title-faint">{(studentName || "Student").split(" ")[0]}.</span></div>
          <div className="fx-sub">Today you&apos;re the librarian of your future.</div>
        </div>

        <div className="fx-hero">
          <div className="fx-hero-glow-a" />
          <div className="fx-hero-glow-b" />
          <div className="fx-hero-pill">CONTINUE LEARNING · DAY</div>
          <div className="fx-hero-title">Today&apos;s focus drill</div>
          <div className="fx-hero-sub">A 15-minute personalised rep across your weakest chapters. Start strong, stay sharp.</div>
          <div className="fx-hero-btn-row">
            <div className="fx-hero-btn fx-hero-btn--primary" />
            <div className="fx-hero-btn fx-hero-btn--ghost" />
          </div>
        </div>

        <div className="fx-kpis">
          {[
            { label: "STREAK", tone: "pink" },
            { label: "TODAY", tone: "lav" },
            { label: "POINTS", tone: "mint" },
            { label: "TOKENS", tone: "amber" },
          ].map((k) => (
            <div key={k.label} className={`fx-kpi fx-kpi--${k.tone}`}>
              <div className="fx-kpi-dot" />
              <div className="fx-kpi-label">{k.label}</div>
              <div className="fx-kpi-value" />
            </div>
          ))}
        </div>

        <div className="fx-jump-row">
          <div className="fx-jump">
            <div className="fx-jump-icon" />
            <div className="fx-jump-label">Study</div>
          </div>
          <div className="fx-jump">
            <div className="fx-jump-icon" />
            <div className="fx-jump-label">Revision</div>
          </div>
          <div className="fx-jump fx-jump--cohort">
            <div className="fx-jump-chip">ACTIVE</div>
            <div className="fx-jump-label">Your Batch</div>
          </div>
        </div>
      </main>
    </div>
  );
}

// ── Foreground modal (native portal look) ──────────────────────────
function FrozenModal({ info, err, user, logout }) {
  const hd = info?.help_desk || {};
  const batchLine = useMemo(
    () => (info?.batches || []).map((b) => b.name).filter(Boolean).join("  ·  ") || "No active batch",
    [info],
  );
  const frozenOn = info?.frozen_at
    ? new Date(info.frozen_at).toLocaleDateString("en-PK", { day: "numeric", month: "short", year: "numeric" })
    : "—";

  return (
    <div className="fx-modal" role="alertdialog" aria-modal="true" aria-labelledby="fx-title" data-testid="frozen-card">
      {/* Brand + status */}
      <header className="fx-head">
        <div className="fx-head-brand">
          <img src="/brand/logo.png" alt="" className="fx-head-logo" />
          <div className="fx-head-brand-text">
            <div className="fx-head-name">LogicsWallah</div>
            <div className="fx-head-sub">Student Portal</div>
          </div>
        </div>
        <span className="fx-pill" data-testid="frozen-status-pill">
          <ShieldAlert size={11} strokeWidth={2.3} /> Restricted
        </span>
      </header>

      {/* Crest + title */}
      <div className="fx-crest">
        <div className="fx-crest-inner">
          <span className="fx-crest-ring" aria-hidden />
          <Lock size={22} strokeWidth={2.2} />
        </div>
      </div>

      <div className="fx-eyebrow">Account status</div>
      <h1 className="fx-title-main" id="fx-title" data-testid="frozen-title">
        Your account has been restricted
      </h1>

      {err ? (
        <div className="fx-error" data-testid="frozen-error">{err}</div>
      ) : (
        <>
          <p className="fx-msg" data-testid="frozen-message">
            {info?.message ||
              "Access to your LogicsWallah portal has been temporarily paused. All your data, batches, and progress remain safely stored — nothing has been lost. Please contact the LW Help Desk below and our team will restore your access shortly."}
          </p>

          {/* Iter 190 — personalized freeze reason. Rendered as a
              distinct callout so students immediately see the exact
              story (streak count, token history, grace history, and
              which threshold triggered the freeze). Hidden when the
              backend has not stamped a reason. */}
          {info?.frozen_reason && (
            <div className="fx-reason" data-testid="frozen-reason">
              <div className="fx-reason-label">Why your account was restricted</div>
              <p className="fx-reason-text">{info.frozen_reason}</p>
            </div>
          )}

          {/* Identity list — labels above values, values wrap so long
              batch names or emails never overflow the viewport. */}
          <dl className="fx-identity" data-testid="frozen-details">
            <IdentityRow label="Student"    value={info?.student_name || user?.name || "—"} testid="fx-id-student" />
            <IdentityRow label="Email"      value={info?.student_email || user?.email || "—"} mono testid="fx-id-email" />
            <IdentityRow label="Batch"      value={batchLine} testid="fx-id-batch" />
            <IdentityRow label="Restricted on" value={frozenOn} testid="fx-id-frozenon" />
          </dl>

          {/* Help desk */}
          <div className="fx-help-title" data-testid="frozen-help-desk-title">
            <span className="fx-help-dot" /> LW Official Help Desk
          </div>

          <div className="fx-help-grid" data-testid="frozen-help-desk">
            {hd.phone && (
              <HelpTile icon={Phone} label="Call us" value={hd.phone}
                        href={`tel:${(hd.phone || "").replace(/\s+/g, "")}`}
                        accent="blue" testid="hd-phone" />
            )}
            {hd.whatsapp && (
              <HelpTile icon={MessageCircle} label="WhatsApp" value={hd.whatsapp}
                        href={`https://wa.me/${(hd.whatsapp || "").replace(/[^0-9]/g, "")}`}
                        external accent="green" testid="hd-whatsapp" />
            )}
            {hd.email && (
              <HelpTile icon={Mail} label="Email" value={hd.email}
                        href={`mailto:${hd.email}?subject=Account%20Restricted%20—%20${encodeURIComponent(info?.student_email || "")}`}
                        accent="amber" testid="hd-email" />
            )}
          </div>

          {hd.hours && (
            <div className="fx-hours" data-testid="hd-hours">
              <Clock size={12} strokeWidth={2.2} />
              <span>{hd.hours}</span>
            </div>
          )}

          <div className="fx-fine">
            <span className="fx-fine-dot" />
            Your data, progress, and enrollments remain fully preserved. The moment our team lifts the restriction, you&apos;ll return exactly where you left off.
          </div>
        </>
      )}

      <button
        onClick={logout}
        className="fx-logout"
        data-testid="frozen-logout-btn"
      >
        <LogOut size={13} strokeWidth={2.2} /> Sign out
      </button>
    </div>
  );
}

// ── Building blocks ─────────────────────────────────────────────────
function IdentityRow({ label, value, mono, testid }) {
  return (
    <div className="fx-id" data-testid={testid}>
      <dt className="fx-id-label">{label}</dt>
      <dd className={`fx-id-value ${mono ? "is-mono" : ""}`}>{value}</dd>
    </div>
  );
}

function HelpTile({ icon: Icon, label, value, href, external, accent, testid }) {
  const [copied, setCopied] = useState(false);
  const doCopy = (e) => {
    e.preventDefault(); e.stopPropagation();
    try {
      navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch { /* silent */ }
  };
  return (
    <a
      className={`fx-help is-${accent}`}
      href={href}
      target={external ? "_blank" : undefined}
      rel={external ? "noreferrer" : undefined}
      data-testid={testid}
    >
      <span className="fx-help-ico"><Icon size={15} strokeWidth={2.2} /></span>
      <span className="fx-help-body">
        <span className="fx-help-label">{label}</span>
        <span className="fx-help-value">{value}</span>
      </span>
      <button
        type="button"
        className="fx-help-copy"
        onClick={doCopy}
        aria-label={`Copy ${label.toLowerCase()}`}
        data-testid={`${testid}-copy`}
      >
        {copied ? <Check size={12} strokeWidth={2.4} /> : <Copy size={12} strokeWidth={2.2} />}
      </button>
    </a>
  );
}

// ── Styles ──────────────────────────────────────────────────────────
function FrozenScreenStyles() {
  return (
    <style>{`
      /* Reset scroll on body for undismissable overlay */
      html, body { overflow: hidden; }

      .fx-shell {
        position: fixed; inset: 0;
        min-height: 100vh; min-height: 100dvh;
        background: var(--px-paper, #eef0f7);
        color: var(--px-ink, #0f172a);
        font-family: "Inter", ui-sans-serif, system-ui, sans-serif;
        overflow: hidden;
        display: flex; align-items: center; justify-content: center;
        padding: 16px;
        z-index: 100;
      }

      /* ─────────── Portal mockup (blurred backdrop) ─────────── */
      .fx-portal {
        position: absolute; inset: 0;
        pointer-events: none;
        display: grid;
        grid-template-columns: 78px 1fr;
        gap: 0;
        transform: scale(1.02);
        filter: blur(12px) saturate(0.9);
        opacity: 0.75;
        overflow: hidden;
      }
      @media (max-width: 640px) {
        .fx-portal { grid-template-columns: 60px 1fr; filter: blur(14px) saturate(0.9); }
      }

      .fx-dock {
        background: var(--px-surface, #fff);
        border-right: 1px solid var(--px-line, rgba(15,23,42,0.08));
        padding: 22px 12px;
        display: flex; flex-direction: column; gap: 12px; align-items: center;
      }
      .fx-dock-brand {
        width: 40px; height: 40px; border-radius: 999px;
        background: linear-gradient(145deg, #3b82f6, #1e40af);
        margin-bottom: 6px;
      }
      .fx-dock-item {
        width: 44px; height: 44px; border-radius: 12px;
        background: rgba(15,23,42,0.04);
        border: 1px solid rgba(15,23,42,0.06);
      }

      .fx-main {
        padding: 34px 40px 20px;
        display: flex; flex-direction: column; gap: 20px;
        min-width: 0;
      }
      @media (max-width: 640px) { .fx-main { padding: 26px 20px; gap: 16px; } }

      .fx-top { display: flex; flex-direction: column; gap: 6px; margin-bottom: 4px; }
      .fx-crumb {
        font-family: "JetBrains Mono", ui-monospace, monospace;
        font-size: 11px; letter-spacing: 0.18em; text-transform: uppercase;
        color: var(--px-ink-mute, #64748b);
      }
      .fx-title {
        font-family: "Space Grotesk", "Inter", sans-serif;
        font-size: 44px; line-height: 1.02; font-weight: 600;
        letter-spacing: -0.02em;
      }
      .fx-title-faint { color: rgba(15,23,42,0.32); }
      .fx-sub {
        font-size: 14px; font-style: italic;
        color: var(--px-ink-mute, #64748b);
        margin-top: 6px;
      }

      /* Hero card */
      .fx-hero {
        position: relative;
        border-radius: 28px;
        padding: 34px 40px;
        background: linear-gradient(135deg, #2a1e5f 0%, #0b1f4d 55%, #081638 100%);
        color: #fff;
        overflow: hidden;
        box-shadow: 0 24px 48px -20px rgba(11,31,77,0.5);
      }
      .fx-hero-glow-a, .fx-hero-glow-b {
        position: absolute; border-radius: 50%; filter: blur(60px); opacity: 0.55;
      }
      .fx-hero-glow-a { top: -100px; right: -80px; width: 340px; height: 340px; background: radial-gradient(circle, #a855f7, transparent 65%); }
      .fx-hero-glow-b { bottom: -140px; left: -60px; width: 320px; height: 320px; background: radial-gradient(circle, #3b82f6, transparent 65%); }
      .fx-hero-pill {
        display: inline-block; padding: 4px 10px; border-radius: 999px;
        background: rgba(255,203,71,0.10); border: 1px solid rgba(255,203,71,0.35);
        color: #ffcb47; font-size: 10px; letter-spacing: 0.14em; font-weight: 700;
        text-transform: uppercase; margin-bottom: 12px;
      }
      .fx-hero-title { font-family: "Space Grotesk", "Inter", sans-serif; font-size: 32px; font-weight: 700; letter-spacing: -0.02em; margin-bottom: 6px; }
      .fx-hero-sub { font-size: 13px; color: rgba(255,255,255,0.75); max-width: 460px; line-height: 1.5; }
      .fx-hero-btn-row { margin-top: 20px; display: flex; gap: 10px; }
      .fx-hero-btn { height: 42px; border-radius: 999px; }
      .fx-hero-btn--primary { width: 180px; background: linear-gradient(135deg, #ec4899, #d946ef); }
      .fx-hero-btn--ghost { width: 140px; background: rgba(255,255,255,0.08); border: 1px solid rgba(255,255,255,0.15); }

      /* KPI cards */
      .fx-kpis { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; }
      @media (max-width: 720px) { .fx-kpis { grid-template-columns: repeat(2, 1fr); } }
      .fx-kpi {
        border-radius: 20px; padding: 16px 18px;
        border: 1px solid rgba(15,23,42,0.05);
        min-height: 96px;
        position: relative;
      }
      .fx-kpi--pink  { background: #fdefef; }
      .fx-kpi--lav   { background: #eeefff; }
      .fx-kpi--mint  { background: #eafaf5; }
      .fx-kpi--amber { background: #fdf0e0; }
      .fx-kpi-dot { width: 34px; height: 34px; border-radius: 10px; background: rgba(15,23,42,0.06); margin-bottom: 12px; }
      .fx-kpi-label { font-size: 10px; letter-spacing: 0.14em; text-transform: uppercase; color: rgba(15,23,42,0.55); margin-bottom: 6px; }
      .fx-kpi-value { height: 26px; width: 60%; border-radius: 6px; background: rgba(15,23,42,0.10); }

      /* Jump row */
      .fx-jump-row { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 14px; }
      @media (max-width: 720px) { .fx-jump-row { grid-template-columns: 1fr 1fr; } }
      .fx-jump {
        min-height: 130px; border-radius: 20px;
        background: var(--px-surface, #fff);
        border: 1px solid rgba(15,23,42,0.05);
        padding: 18px; display: flex; flex-direction: column; justify-content: space-between;
      }
      .fx-jump-icon { width: 40px; height: 40px; border-radius: 12px; background: linear-gradient(145deg, #0f172a, #1e293b); }
      .fx-jump-label { font-size: 15px; font-weight: 500; }
      .fx-jump--cohort { background: #fdf0e0; }
      .fx-jump-chip { display: inline-block; padding: 3px 8px; border-radius: 999px; font-size: 10px; font-weight: 700; letter-spacing: 0.10em; background: #d1fae5; color: #065f46; align-self: flex-start; }

      /* ─────────── Dim veil over the mockup ─────────── */
      .fx-dim {
        position: absolute; inset: 0;
        background:
          radial-gradient(circle at 50% 42%, rgba(238,240,247,0.35), rgba(238,240,247,0.82) 78%),
          linear-gradient(180deg, rgba(238,240,247,0.55), rgba(238,240,247,0.85));
        pointer-events: none;
      }

      /* ─────────── Foreground modal ─────────── */
      .fx-modal-wrap {
        position: relative; z-index: 2;
        width: 100%;
        max-width: 560px;
        display: flex; justify-content: center;
      }
      .fx-modal {
        width: 100%;
        background: var(--px-surface, #fff);
        border-radius: 24px;
        padding: 28px 28px 24px;
        box-shadow:
          0 24px 60px -20px rgba(15,23,42,0.28),
          0 6px 18px -6px rgba(15,23,42,0.12),
          0 0 0 1px rgba(15,23,42,0.05) inset;
        max-height: calc(100dvh - 32px);
        overflow-y: auto;
      }
      .fx-modal::-webkit-scrollbar { width: 6px; }
      .fx-modal::-webkit-scrollbar-thumb { background: rgba(15,23,42,0.15); border-radius: 999px; }
      @media (max-width: 480px) {
        .fx-modal { padding: 22px 20px 20px; border-radius: 20px; }
      }
      .fx-modal--loading {
        min-height: 200px;
        display: flex; align-items: center; justify-content: center;
      }

      /* Header */
      .fx-head { display: flex; align-items: center; gap: 12px; margin-bottom: 22px; }
      .fx-head-brand { display: flex; align-items: center; gap: 10px; min-width: 0; flex: 1; }
      .fx-head-logo {
        width: 36px; height: 36px; border-radius: 10px;
        background: var(--px-paper, #eef0f7);
        border: 1px solid var(--px-line, rgba(15,23,42,0.08));
        padding: 4px; object-fit: contain; flex-shrink: 0;
      }
      .fx-head-brand-text { min-width: 0; line-height: 1.15; }
      .fx-head-name {
        font-family: "Space Grotesk", "Inter", sans-serif;
        font-weight: 700; letter-spacing: -0.01em;
        font-size: 15px; color: var(--px-ink, #0f172a);
      }
      .fx-head-sub {
        font-family: "JetBrains Mono", ui-monospace, monospace;
        font-size: 9.5px; letter-spacing: 0.18em; text-transform: uppercase;
        color: var(--px-ink-mute, #64748b); margin-top: 2px;
      }
      .fx-pill {
        display: inline-flex; align-items: center; gap: 5px;
        padding: 5px 10px; border-radius: 999px;
        font-size: 10px; font-weight: 700; letter-spacing: 0.10em; text-transform: uppercase;
        color: #b45309;
        background: #fef3c7;
        border: 1px solid #f59e0b;
        white-space: nowrap;
        flex-shrink: 0;
      }

      /* Crest */
      .fx-crest { display: flex; margin-bottom: 12px; }
      .fx-crest-inner {
        position: relative;
        width: 52px; height: 52px; border-radius: 16px;
        display: flex; align-items: center; justify-content: center;
        background: #fef3c7;
        border: 1px solid #f59e0b;
        color: #b45309;
      }
      .fx-crest-ring {
        position: absolute; inset: -6px; border-radius: 22px;
        border: 1px dashed rgba(245,158,11,0.45);
        animation: fxRing 14s linear infinite;
      }
      @keyframes fxRing { to { transform: rotate(360deg); } }

      .fx-eyebrow {
        font-family: "JetBrains Mono", ui-monospace, monospace;
        font-size: 10px; letter-spacing: 0.18em; text-transform: uppercase;
        color: #b45309; font-weight: 700;
        margin-bottom: 4px;
      }
      .fx-title-main {
        font-family: "Space Grotesk", "Inter", sans-serif;
        font-size: 24px; line-height: 1.18; font-weight: 700; letter-spacing: -0.02em;
        color: var(--px-ink, #0f172a);
        margin: 0 0 12px;
      }
      @media (min-width: 520px) {
        .fx-title-main { font-size: 28px; }
      }

      .fx-msg {
        font-size: 13.5px; line-height: 1.6;
        color: var(--px-ink-mute, #64748b);
        margin: 0 0 20px;
      }
      .fx-error {
        margin-bottom: 20px; padding: 12px 14px;
        border-radius: 12px;
        background: #fef2f2; border: 1px solid #fecaca; color: #b91c1c;
        font-size: 12.5px;
      }

      /* Personalized freeze reason — Iter 190. Rendered as a soft
         amber callout so it stands out from the generic message
         without feeling punitive. */
      .fx-reason {
        margin: 0 0 20px;
        padding: 14px 16px;
        border-radius: 14px;
        background: linear-gradient(180deg, #fff7ed, #fffbeb);
        border: 1px solid #fed7aa;
      }
      .fx-reason-label {
        font-family: "JetBrains Mono", ui-monospace, monospace;
        font-size: 9.5px; letter-spacing: 0.16em; text-transform: uppercase;
        color: #b45309;
        margin: 0 0 6px;
        font-weight: 700;
      }
      .fx-reason-text {
        font-size: 13px; line-height: 1.55;
        color: #78350f;
        margin: 0;
        word-break: break-word;
        overflow-wrap: anywhere;
      }

      /* Identity list */
      .fx-identity {
        margin: 0 0 22px;
        padding: 14px 16px;
        border-radius: 14px;
        background: var(--px-paper, #eef0f7);
        border: 1px solid var(--px-line, rgba(15,23,42,0.08));
        display: flex; flex-direction: column; gap: 12px;
      }
      .fx-id { display: flex; flex-direction: column; gap: 3px; min-width: 0; }
      .fx-id-label {
        font-family: "JetBrains Mono", ui-monospace, monospace;
        font-size: 9.5px; letter-spacing: 0.16em; text-transform: uppercase;
        color: var(--px-ink-mute, #64748b);
        margin: 0;
      }
      .fx-id-value {
        font-size: 13.5px; font-weight: 600; color: var(--px-ink, #0f172a);
        margin: 0;
        word-break: break-word;
        overflow-wrap: anywhere;
        line-height: 1.35;
      }
      .fx-id-value.is-mono {
        font-family: "JetBrains Mono", ui-monospace, monospace;
        font-size: 12.5px; font-weight: 500; color: #334155;
      }

      /* Help desk */
      .fx-help-title {
        display: flex; align-items: center; gap: 8px;
        font-family: "JetBrains Mono", ui-monospace, monospace;
        font-size: 10px; letter-spacing: 0.18em; text-transform: uppercase;
        color: var(--px-ink-mute, #64748b);
        margin-bottom: 10px;
      }
      .fx-help-dot {
        width: 6px; height: 6px; border-radius: 50%;
        background: #3b82f6;
        box-shadow: 0 0 8px rgba(59,130,246,0.5);
      }

      .fx-help-grid { display: flex; flex-direction: column; gap: 8px; margin-bottom: 14px; }
      .fx-help {
        position: relative;
        display: flex; align-items: center; gap: 12px;
        padding: 12px 14px;
        border-radius: 14px;
        background: #fff;
        border: 1px solid var(--px-line, rgba(15,23,42,0.08));
        text-decoration: none; color: inherit;
        transition: border-color 180ms, background 180ms, transform 180ms;
        min-width: 0;
      }
      .fx-help:hover { transform: translateY(-1px); background: #fafbff; }
      .fx-help-ico {
        width: 36px; height: 36px; border-radius: 10px;
        display: flex; align-items: center; justify-content: center;
        flex-shrink: 0;
      }
      .fx-help-body { min-width: 0; flex: 1; display: flex; flex-direction: column; gap: 2px; }
      .fx-help-label {
        font-family: "JetBrains Mono", ui-monospace, monospace;
        font-size: 9.5px; letter-spacing: 0.14em; text-transform: uppercase;
        color: var(--px-ink-mute, #64748b);
      }
      .fx-help-value {
        font-size: 14px; font-weight: 600; color: var(--px-ink, #0f172a);
        white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
      }
      .fx-help-copy {
        flex-shrink: 0;
        width: 30px; height: 30px; border-radius: 9px;
        background: var(--px-paper, #eef0f7);
        border: 1px solid var(--px-line, rgba(15,23,42,0.08));
        color: var(--px-ink-mute, #64748b);
        display: flex; align-items: center; justify-content: center;
        cursor: pointer; transition: 180ms;
      }
      .fx-help-copy:hover { background: #e0e6f2; color: var(--px-ink, #0f172a); }

      .fx-help.is-blue  .fx-help-ico { background: #eff6ff; color: #2563eb; border: 1px solid #bfdbfe; }
      .fx-help.is-blue:hover  { border-color: #93c5fd; }
      .fx-help.is-green .fx-help-ico { background: #ecfdf5; color: #059669; border: 1px solid #a7f3d0; }
      .fx-help.is-green:hover { border-color: #6ee7b7; }
      .fx-help.is-amber .fx-help-ico { background: #fef3c7; color: #b45309; border: 1px solid #fcd34d; }
      .fx-help.is-amber:hover { border-color: #f59e0b; }

      .fx-hours {
        display: inline-flex; align-items: center; gap: 6px;
        padding: 5px 10px;
        margin-bottom: 20px;
        border-radius: 999px;
        background: var(--px-paper, #eef0f7);
        border: 1px solid var(--px-line, rgba(15,23,42,0.08));
        font-size: 11.5px;
        color: var(--px-ink-mute, #64748b);
      }
      .fx-hours svg { color: #94a3b8; }

      .fx-fine {
        display: flex; align-items: flex-start; gap: 8px;
        font-size: 11.5px; line-height: 1.6;
        color: var(--px-ink-mute, #64748b);
        padding: 12px 14px;
        border-radius: 12px;
        background: #eff6ff;
        border: 1px solid #bfdbfe;
        margin-bottom: 20px;
      }
      .fx-fine-dot {
        width: 6px; height: 6px; border-radius: 50%; background: #3b82f6;
        flex-shrink: 0; margin-top: 6px;
      }

      .fx-logout {
        width: 100%;
        height: 42px;
        display: flex; align-items: center; justify-content: center; gap: 6px;
        border-radius: 12px;
        background: var(--px-paper, #eef0f7);
        border: 1px solid var(--px-line, rgba(15,23,42,0.08));
        color: var(--px-ink-soft, #334155);
        font-size: 13px; font-weight: 500;
        cursor: pointer; transition: 180ms;
      }
      .fx-logout:hover { background: #e0e6f2; color: var(--px-ink, #0f172a); }
    `}</style>
  );
}
